import { useState } from "react";
import { notification } from "antd";
import { SyncOutlined, PrinterOutlined, CopyOutlined, CalendarOutlined, CalendarFilled } from "@ant-design/icons";
import { FeedbackView } from "./FeedbackView";
import { ScheduleRegenModalView, ScheduleData } from "../../../assets/utilities/ScheduleRegenModalView";
import { NLIJobService } from "../../../../services/NLIJobService";
import { ConditionalTooltip } from "./ConditionalTooltip";
import { useIsReadOnly } from "redux/hooks";

export function JobActionButtonsView(props: {
    canDownloadOutput: boolean;
    isOutputLoaded: boolean;
    loadedOutput: any;
    darkMode: boolean;
    parentJobAlias?: string;
    isRegenerating: boolean;
    onRegenerate: () => void;
    feedbackUpViewModel: any;
    feedbackDownViewModel: any;
    jobID: string;
    jobService: NLIJobService;
    initialScheduleTime?: string;
    initialScheduleType?: string;
    onScheduleUpdate?: () => Promise<void>;
}) {
    const { canDownloadOutput, isOutputLoaded, loadedOutput, darkMode, parentJobAlias, isRegenerating, onRegenerate, feedbackUpViewModel, feedbackDownViewModel, jobID, jobService, initialScheduleTime, initialScheduleType, onScheduleUpdate } = props;
    const isReadOnly = useIsReadOnly();
    const [isReloadHovered, setIsReloadHovered] = useState(false);
    const [isPdfHovered, setIsPdfHovered] = useState(false);
    const [pressedPdf, setPressedPdf] = useState(false);
    const [isCopyResponseHovered, setIsCopyResponseHovered] = useState(false);
    const [pressedResponse, setPressedResponse] = useState(false);
    const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
    const [isScheduleHovered, setIsScheduleHovered] = useState(false);
    const [pressedSchedule, setPressedSchedule] = useState(false);

    if (!canDownloadOutput || !isOutputLoaded) {
        return null;
    }

    return (
        <div style={{
            display: "flex",
            gap: "0.75em",
            marginTop: "0.25em"
        }}>
            <ConditionalTooltip title={isReadOnly ? "Read-only — leave Admin View to regenerate" : "Regenerate response"}>
                <button
                    onClick={onRegenerate}
                    disabled={isRegenerating || isReadOnly}
                    onMouseEnter={() => setIsReloadHovered(true)}
                    onMouseLeave={() => setIsReloadHovered(false)}
                    style={{
                        background: "transparent",
                        border: "none",
                        cursor: (isRegenerating || isReadOnly) ? "not-allowed" : "pointer",
                        fontSize: "20px",
                        color: (isRegenerating || isReadOnly)
                            ? (darkMode ? "#666666" : "#CCCCCC")
                            : (isReloadHovered
                                ? (darkMode ? "#ffffff" : "#000000")
                                : (darkMode ? "#888888" : "#AFAFAF")),
                        display: "flex",
                        alignItems: "center",
                        padding: "0",
                        transition: "color 0.2s, transform 0.1s"
                    }}
                >
                    <SyncOutlined />
                </button>
            </ConditionalTooltip>
            <ConditionalTooltip title={isReadOnly ? "Read-only — leave Admin View to schedule" : "Schedule regeneration"}>
                <button
                    onClick={() => {
                        if (isReadOnly) return;
                        setPressedSchedule(true);
                        setTimeout(() => setPressedSchedule(false), 200);
                        setIsScheduleModalOpen(true);
                    }}
                    disabled={isReadOnly}
                    onMouseEnter={() => setIsScheduleHovered(true)}
                    onMouseLeave={() => setIsScheduleHovered(false)}
                    style={{
                        background: "transparent",
                        border: "none",
                        cursor: isReadOnly ? "not-allowed" : "pointer",
                        fontSize: "20px",
                        color: isReadOnly
                            ? (darkMode ? "#666666" : "#CCCCCC")
                            : (isScheduleHovered
                                ? (darkMode ? "#ffffff" : "#000000")
                                : (darkMode ? "#888888" : "#AFAFAF")),
                        display: "flex",
                        alignItems: "center",
                        padding: "0",
                        transition: "color 0.2s, transform 0.1s",
                        transform: pressedSchedule ? "scale(0.85)" : "scale(1)"
                    }}
                >
                    {initialScheduleTime && initialScheduleType ? <CalendarFilled /> : <CalendarOutlined />}
                </button>
            </ConditionalTooltip>
            <ConditionalTooltip title="Export to PDF">
                <button
                    onClick={async () => {
                        if (!isOutputLoaded || !loadedOutput?.ResponsePdf) {
                            notification.error({
                                message: 'PDF not available',
                                description: 'The PDF output is not available for this job.',
                                placement: 'bottomLeft',
                                duration: 3
                            });
                            return;
                        }
                        setPressedPdf(true);
                        setTimeout(() => setPressedPdf(false), 200);
                        try {
                            // Use the presigned URL directly from resultLoader
                            const pdfUrl = loadedOutput.ResponsePdf.content;
                            const response = await fetch(pdfUrl);
                            if (!response.ok) throw new Error('Failed to fetch PDF');
                            const blob = await response.blob();
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            const filename = parentJobAlias ? `${parentJobAlias}.pdf` : `${loadedOutput.ResponsePdf.name}.pdf`;
                            a.setAttribute('download', filename);
                            a.setAttribute('href', url);
                            a.click();
                            URL.revokeObjectURL(url);
                        } catch (error) {
                            notification.error({
                                message: 'Failed to download PDF',
                                description: 'Please try again.',
                                placement: 'bottomLeft',
                                duration: 3
                            });
                        }
                    }}
                    disabled={!isOutputLoaded || !loadedOutput?.ResponsePdf}
                    onMouseEnter={() => setIsPdfHovered(true)}
                    onMouseLeave={() => setIsPdfHovered(false)}
                    style={{
                        background: "transparent",
                        border: "none",
                        cursor: (!isOutputLoaded || !loadedOutput?.ResponsePdf) ? "not-allowed" : "pointer",
                        fontSize: "20px",
                        color: (!isOutputLoaded || !loadedOutput?.ResponsePdf)
                            ? (darkMode ? "#666666" : "#CCCCCC")
                            : (isPdfHovered
                                ? (darkMode ? "#ffffff" : "#000000")
                                : (darkMode ? "#888888" : "#AFAFAF")),
                        display: "flex",
                        alignItems: "center",
                        padding: "0",
                        transition: "color 0.2s, transform 0.1s",
                        transform: pressedPdf ? "scale(0.85)" : "scale(1)"
                    }}
                >
                    <PrinterOutlined />
                </button>
            </ConditionalTooltip>
            <ConditionalTooltip title="Copy response">
                <button
                    onClick={async () => {
                        const response = loadedOutput?.Response?.content || loadedOutput?.Data || loadedOutput?.Error?.content || "";
                        const text = typeof response === 'string' ? response.trim() : JSON.stringify(response).trim();
                        try {
                            await navigator.clipboard.writeText(text);
                            setPressedResponse(true);
                            setTimeout(() => setPressedResponse(false), 200);
                            notification.success({
                                message: 'Copied to clipboard',
                                description: 'Response has been copied to your clipboard.',
                                placement: 'bottomLeft',
                                duration: 2
                            });
                        } catch (error) {
                            notification.error({
                                message: 'Failed to copy',
                                description: 'Could not copy to clipboard. Please try again.',
                                placement: 'bottomLeft',
                                duration: 3
                            });
                        }
                    }}
                    onMouseEnter={() => setIsCopyResponseHovered(true)}
                    onMouseLeave={() => setIsCopyResponseHovered(false)}
                    style={{
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        fontSize: "20px",
                        color: isCopyResponseHovered
                            ? (darkMode ? "#ffffff" : "#000000")
                            : (darkMode ? "#888888" : "#AFAFAF"),
                        display: "flex",
                        alignItems: "center",
                        padding: "0",
                        transition: "color 0.2s, transform 0.1s",
                        transform: pressedResponse ? "scale(0.85)" : "scale(1)"
                    }}
                >
                    <CopyOutlined />
                </button>
            </ConditionalTooltip>
            <FeedbackView model={feedbackUpViewModel} />
            <FeedbackView model={feedbackDownViewModel} />
            <ScheduleRegenModalView
                isOpen={isScheduleModalOpen}
                onClose={() => setIsScheduleModalOpen(false)}
                initialScheduleTime={initialScheduleTime}
                initialScheduleType={initialScheduleType}
                onSchedule={async (schedule: ScheduleData) => {
                    try {
                        // Check if this is a clear schedule request
                        if (schedule.clearSchedule) {
                            await jobService.scheduleJob(jobID, null, null);
                            if (onScheduleUpdate) {
                                await onScheduleUpdate();
                            }
                            notification.success({
                                message: 'Schedule stopped',
                                description: 'The schedule has been cleared.',
                                placement: 'bottomLeft',
                                duration: 3
                            });
                        } else {
                            // Convert Dayjs to ISO 8601 string
                            const scheduleTime = schedule.date.toISOString();
                            // Map frequency to scheduleType
                            const scheduleType = schedule.frequency;

                            await jobService.scheduleJob(jobID, scheduleTime, scheduleType);
                            if (onScheduleUpdate) {
                                await onScheduleUpdate();
                            }

                            notification.success({
                                message: initialScheduleTime && initialScheduleType ? 'Schedule updated' : 'Schedule created',
                                description: `Regeneration scheduled for ${schedule.date.format('YYYY-MM-DD h:mm A')} (${schedule.frequency}).`,
                                placement: 'bottomLeft',
                                duration: 3
                            });
                        }
                    } catch (error) {
                        notification.error({
                            message: 'Failed to update schedule',
                            description: error instanceof Error ? error.message : 'Please try again.',
                            placement: 'bottomLeft',
                            duration: 3
                        });
                        throw error; // Re-throw to prevent modal from closing
                    }
                }}
                darkMode={darkMode}
            />
        </div>
    );
};

